import type { HeroSlide } from '../lib/catalog'
import { DWELL_HERO_MS, FADE_MS } from '../config/board'
import { PhotoPlaceholder } from './PhotoPlaceholder'

// The only thing on the board that moves. The crossfade is pure CSS: every slide runs
// the same keyframes over an n × 8 s cycle and takes its turn through a negative
// animation-delay — no JS timer, no repaint beyond opacity (the panel's GPU budget is
// small). Screen 2 subtracts a further half slide so the two walls never flip together.

function keyframes(n: number): string {
  const cycle = (n * DWELL_HERO_MS) / 1000
  const visible = 100 / n
  const fade = (100 * (FADE_MS / 1000)) / cycle
  return (
    '@keyframes heroFade {' +
    ' 0% { opacity: 0; }' +
    ' ' + fade.toFixed(3) + '% { opacity: 1; }' +
    ' ' + visible.toFixed(3) + '% { opacity: 1; }' +
    ' ' + (visible + fade).toFixed(3) + '% { opacity: 0; }' +
    ' 100% { opacity: 0; } }'
  )
}

export function HeroPanel({ slides, offsetMs = 0 }: { slides: HeroSlide[]; offsetMs?: number }) {
  const n = slides.length
  const cycle = (n * DWELL_HERO_MS) / 1000
  return (
    <div className="relative flex-none w-[640px] h-full rounded-[24px] overflow-hidden bg-brand-ink border-2 border-brand-line">
      {n > 1 ? <style>{keyframes(n)}</style> : null}
      {slides.map((slide, i) => (
        <div
          key={slide.key}
          className="absolute inset-0"
          style={
            n > 1
              ? {
                  // Longhand only: React warns when the `animation` shorthand and
                  // `animationDelay` are set together and the delay changes on rerender.
                  opacity: 0,
                  animationName: 'heroFade',
                  animationDuration: cycle + 's',
                  animationTimingFunction: 'ease-in-out',
                  animationIterationCount: 'infinite',
                  animationDelay: -(i * (DWELL_HERO_MS / 1000)) - offsetMs / 1000 + 's',
                }
              : undefined
          }
        >
          {slide.image_url ? (
            <img src={slide.image_url} alt="" className="absolute inset-0 block w-full h-full object-cover" />
          ) : (
            <PhotoPlaceholder />
          )}
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(39,32,27,0)_42%,rgba(39,32,27,0.88)_100%)]" />
          <div className="absolute left-[44px] right-[44px] bottom-[44px] flex flex-col space-y-[12px]">
            {slide.kicker ? (
              <div className="font-secondary font-bold text-[22px] leading-none tracking-[0.14em] uppercase text-brand-butterscotch">
                {slide.kicker}
              </div>
            ) : null}
            <div className="font-primary font-bold text-[64px] leading-none text-brand-parchment">{slide.title}</div>
            {slide.sub || slide.price ? (
              <div className="flex items-baseline justify-between space-x-[20px]">
                {slide.sub ? (
                  <div className="font-secondary font-normal text-[30px] leading-[1.3] text-brand-parchment/85">
                    {slide.sub}
                  </div>
                ) : null}
                {slide.price ? (
                  <div className="font-secondary font-bold text-[48px] leading-none text-brand-parchment whitespace-nowrap">
                    {slide.price}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}
